// src/slices/prizeStructure/thunk.ts
// Async thunks cho Cơ cấu Giải thưởng (cấu hình tĩnh 10 hạng giải, dùng chung mọi kỳ) — theo từng Miền.
//
// Rewrite: bỏ toàn bộ thunk theo issuancePlanId (fetchPrizeStructureByPlan/createLotteryResult/
// updatePrizeStructureEntry/fetchIssuancePlanOptions) — dữ liệu theo kỳ nay thuộc slices/drawResult/thunk.ts.

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getPrizeTierConfigsApi,
  updatePrizeTierConfigsApi,
  createPrizeTierConfigApi,
  initializePrizeTierConfigsApi,
  deletePrizeTierConfigApi,
} from 'helpers/fakebackend_helper';
import { toast } from 'react-toastify';
import { UpdatePrizeTierConfigItem, NewPrizeTierConfigInput } from 'pages/PrizeStructure/types';

// Danh mục 10 hạng giải cố định kèm Quantity/ValuePerTicket/TotalValue/MatchingRule, của 1 miền
export const fetchPrizeTierConfigs = createAsyncThunk(
  'PrizeStructure/fetchPrizeTierConfigs',
  async (region: string, { rejectWithValue }) => {
    try {
      const response = await getPrizeTierConfigsApi(region);
      return (response as any) || [];
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Không thể tải cấu hình hạng giải';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Lưu cấu hình cả 10 hạng giải cùng lúc (1 nút "Lưu cấu hình") của 1 miền
export const updatePrizeTierConfigs = createAsyncThunk(
  'PrizeStructure/updatePrizeTierConfigs',
  async ({ region, tiers }: { region: string; tiers: UpdatePrizeTierConfigItem[] }, { rejectWithValue }) => {
    try {
      const response = await updatePrizeTierConfigsApi({ region, tiers });
      toast.success('Lưu cấu hình giải thưởng thành công.');
      return (response as any) || [];
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Không thể lưu cấu hình giải thưởng';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Thêm 1 hạng giải mới vào cơ cấu của 1 miền ("+ Thêm hạng giải")
export const createPrizeTierConfig = createAsyncThunk(
  'PrizeStructure/createPrizeTierConfig',
  async ({ region, tier }: { region: string; tier: NewPrizeTierConfigInput }, { rejectWithValue }) => {
    try {
      const response = await createPrizeTierConfigApi({ region, ...tier });
      toast.success('Đã thêm hạng giải mới.');
      return response as any;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Không thể thêm hạng giải mới';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Khởi tạo 10 hạng giải mặc định khi cơ cấu của 1 miền đang rỗng (môi trường chưa từng seed dữ liệu, hoặc lần
// đầu cấu hình cho Miền Trung/Miền Nam — xem empty state ở FE)
export const initializePrizeTierConfigs = createAsyncThunk(
  'PrizeStructure/initializePrizeTierConfigs',
  async (region: string, { rejectWithValue }) => {
    try {
      const response = await initializePrizeTierConfigsApi(region);
      toast.success('Khởi tạo cơ cấu giải thưởng thành công.');
      return (response as any) || [];
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Không thể khởi tạo cơ cấu giải thưởng';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Xoá 1 hạng giải khỏi cơ cấu của 1 miền (VD bỏ hẳn Giải Tám nếu không dùng)
export const deletePrizeTierConfig = createAsyncThunk(
  'PrizeStructure/deletePrizeTierConfig',
  async ({ region, code }: { region: string; code: string }, { rejectWithValue }) => {
    try {
      await deletePrizeTierConfigApi(region, code);
      toast.success('Đã xoá hạng giải.');
      return code;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Không thể xoá hạng giải';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);
